import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useQuestionnaire } from '../context/QuestionnaireContext';
import { questionSections } from '../data/questions';

const { FiArrowLeft, FiArrowRight, FiCheck } = FiIcons;

function QuestionnairePage({ currentStep, setCurrentStep, totalSteps }) {
  const navigate = useNavigate();
  const { responses, updateResponse, completeQuestionnaire } = useQuestionnaire();
  const [localResponses, setLocalResponses] = useState({});

  const currentSection = questionSections[currentStep];

  useEffect(() => {
    // Initialize local responses with existing data
    const sectionResponses = {};
    currentSection?.questions.forEach(q => {
      sectionResponses[q.id] = responses[q.id] || '';
    });
    setLocalResponses(sectionResponses);
  }, [currentStep, currentSection, responses]);

  const handleInputChange = (questionId, value) => {
    setLocalResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleNext = () => {
    // Save all local responses to context
    Object.entries(localResponses).forEach(([questionId, value]) => {
      updateResponse(questionId, value);
    });

    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeQuestionnaire();
      navigate('/results');
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate('/');
    }
  };

  const isStepComplete = () => {
    return currentSection?.questions.every(q => {
      if (q.required) {
        const value = localResponses[q.id];
        return value && value.toString().trim() !== '';
      }
      return true;
    });
  };

  if (!currentSection) {
    return <div>Loading...</div>;
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="bg-white rounded-2xl shadow-xl p-8"
    >
      {/* Section Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
            <SafeIcon icon={currentSection.icon} className="text-white text-lg" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800">{currentSection.title}</h2>
        </div>
        {currentSection.description && (
          <p className="text-gray-600 text-lg">{currentSection.description}</p>
        )}
      </div>

      {/* Questions */}
      <div className="space-y-8 mb-8">
        <AnimatePresence mode="wait">
          {currentSection.questions.map((question, index) => (
            <motion.div
              key={question.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="space-y-3"
            >
              <label className="block text-lg font-semibold text-gray-800">
                {question.text}
                {question.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              
              {question.subtitle && (
                <p className="text-gray-600 text-sm">{question.subtitle}</p>
              )}

              {renderQuestionInput(question, localResponses[question.id], handleInputChange)}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center pt-6 border-t border-gray-200">
        <button
          onClick={handlePrevious}
          className="inline-flex items-center gap-2 px-6 py-3 text-gray-600 hover:text-gray-800 font-medium transition-colors"
        >
          <SafeIcon icon={FiArrowLeft} />
          {currentStep === 0 ? 'Back to Start' : 'Previous'}
        </button>

        <button
          onClick={handleNext}
          disabled={!isStepComplete()}
          className={`inline-flex items-center gap-2 px-8 py-3 rounded-xl font-semibold transition-all ${
            isStepComplete()
              ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:from-blue-600 hover:to-purple-700 shadow-lg hover:shadow-xl'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          {currentStep === totalSteps - 1 ? (
            <>
              Complete <SafeIcon icon={FiCheck} />
            </>
          ) : (
            <>
              Next <SafeIcon icon={FiArrowRight} />
            </>
          )}
        </button>
      </div>
    </motion.div>
  );
}

function renderQuestionInput(question, value, onChange) {
  switch (question.type) {
    case 'text':
      return (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(question.id, e.target.value)}
          placeholder={question.placeholder}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
        />
      );

    case 'textarea':
      return (
        <textarea
          value={value || ''}
          onChange={(e) => onChange(question.id, e.target.value)}
          placeholder={question.placeholder}
          rows={question.rows || 4}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-vertical"
        />
      );

    case 'number':
      return (
        <input
          type="number"
          value={value || ''}
          onChange={(e) => onChange(question.id, e.target.value)}
          placeholder={question.placeholder}
          min={question.min}
          max={question.max}
          step={question.step}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
        />
      );

    case 'select':
      return (
        <select
          value={value || ''}
          onChange={(e) => onChange(question.id, e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
        >
          <option value="">Select an option...</option>
          {question.options.map((option, index) => (
            <option key={index} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );

    case 'checkbox':
      return (
        <div className="space-y-3">
          {question.options.map((option, index) => (
            <label key={index} className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={(value || []).includes(option.value)}
                onChange={(e) => {
                  const currentValues = value || [];
                  const newValues = e.target.checked
                    ? [...currentValues, option.value]
                    : currentValues.filter(v => v !== option.value);
                  onChange(question.id, newValues);
                }}
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-gray-700">{option.label}</span>
            </label>
          ))}
        </div>
      );

    case 'radio':
      return (
        <div className="space-y-3">
          {question.options.map((option, index) => (
            <label key={index} className="flex items-center gap-3 cursor-pointer">
              <input
                type="radio"
                name={question.id}
                value={option.value}
                checked={value === option.value}
                onChange={(e) => onChange(question.id, e.target.value)}
                className="w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <span className="text-gray-700">{option.label}</span>
            </label>
          ))}
        </div>
      );

    case 'scale':
      return (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">{question.scaleLabels?.min || 'Low'}</span>
            <span className="text-sm text-gray-500">{question.scaleLabels?.max || 'High'}</span>
          </div>
          <input
            type="range"
            min={question.min || 1}
            max={question.max || 10}
            value={value || question.min || 1}
            onChange={(e) => onChange(question.id, e.target.value)}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
          />
          <div className="text-center">
            <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-semibold">
              {value || question.min || 1}
            </span>
          </div>
        </div>
      );

    default:
      return null;
  }
}

export default QuestionnairePage;